import api from './client';
import { storage } from '../utils/storage';
import { persistRefreshToken, readRefreshToken } from '../utils/authTransport';
import { announceSignOut, SIGN_OUT_REASONS } from '../modules/auth/sessionSignals';

export const register = (payload) =>
  api.post('/register', payload);

/**
 * Store a session body — but only one that IS a session.
 *
 * A login for an account with two-step verification answers
 * `{ mfaRequired, challengeId }` and no token. This used to store
 * `accessToken: undefined` regardless, and the caller carried on as if the
 * person were signed in. Every writer below goes through here, so none of
 * them can store a half-session again.
 */
const storeSession = (data) => {
  if (!data?.accessToken) return data;
  storage.set(storage.KEYS.ACCESS_TOKEN, data.accessToken);
  // No-op in cookie mode — see utils/authTransport.js.
  persistRefreshToken(data.refreshToken, data.transport);
  return data;
};

/**
 * Resolves to the session, or to `{ mfaRequired: true, challengeId, … }` when
 * a code is still needed — finish that with `completeMfaLogin`.
 */
export const login = async (payload) => {
  const res = await api.post('/login', payload);
  return storeSession(res?.data?.data ?? res?.data ?? {});
};

/** Second step of a two-step sign-in: `{ challengeId, code }` or `{ challengeId, recoveryCode }`. */
export const completeMfaLogin = async (payload = {}) => {
  const res = await api.post('/auth/mfa/complete', payload);
  return storeSession(res?.data?.data ?? res?.data ?? {});
};

export const logout = async () => {
  // Other tabs hear about it first — a navigation straight after this call can
  // cancel the request below. keepRecent: an idle expiry that already said
  // "expired" is not relabelled as a sign-out.
  announceSignOut(SIGN_OUT_REASONS.SIGNED_OUT, { keepRecent: true });
  // In cookie mode the gateway reads the refresh cookie off the request, so
  // an empty body is correct and the call must still be made — skipping it
  // would leave the server-side session alive after a "log out".
  const refreshToken = readRefreshToken();
  await api.post('/logout', refreshToken ? { refreshToken } : {}).catch(() => {});
  storage.clear();
};

export const refresh = (refreshToken) =>
  api.post('/refresh', { refreshToken });

/**
 * Which external providers can actually complete a sign-in right now.
 *
 * Server-decided, not hardcoded here. A provider is only listed once its
 * client credentials exist in the environment, so the page cannot offer a
 * button that redirects into an OAuth error — and an environment with none
 * configured correctly renders no buttons at all rather than four broken
 * ones.
 */
export const externalProviders = () =>
  api.get('/auth/external/providers').then((r) => {
    const data = r?.data?.data ?? r?.data ?? {};
    return Array.isArray(data.providers) ? data.providers : [];
  });

/**
 * Begin the round-trip. POST, because the auth service distinguishes a
 * sign-in from an account link by the body — a detail that cost this feature
 * its entire working state: only GET was registered service-side, so every
 * attempt 404'd.
 */
export const startExternalLogin = (provider) =>
  api.post(`/auth/external/${provider}/login`, { mode: 'login' })
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

/**
 * Hand the provider's `code` + `state` to the gateway.
 *
 * Over XHR from the app, NOT by letting the browser land on the API. The
 * gateway answers this in JSON, so a redirect URI pointing straight at it
 * showed the visitor a page of raw JSON containing their access token — which
 * is exactly what happened before this callback route existed.
 *
 * Three possible answers, all 200: signed in (tokens), `requiresRegistration`,
 * or `requiresManualLink`.
 */
export const completeExternalCallback = (provider, { code, state }) =>
  api.get(`/auth/external/${provider}/callback`, { params: { code, state } })
    .then((r) => {
      // A successful sign-in returns tokens; store them the same way a
      // password login does so the rest of the app sees one shape of session.
      return storeSession(r?.data?.data ?? r?.data ?? {});
    });

export const completeExternalRegistration = (payload) =>
  api.post('/auth/external/register-complete', payload)
    .then((r) => storeSession(r?.data?.data ?? r?.data ?? {}));

/**
 * Attach a pending provider identity to the account you have just signed into.
 *
 * Requires a session — that is the whole control. The `linkToken` says WHICH
 * identity, the session says WHOSE account, and the gateway refuses unless the
 * token's email matches the session's.
 */
export const completeExternalLink = ({ linkToken }) =>
  api.post('/auth/external/link-complete', { linkToken })
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

// ─── Operator: identity-provider configuration ──────────────────────────
//
// The settings surface behind /admin/auth/providers. Distinct from
// `externalProviders()` above, which is the PUBLIC list a sign-in page draws
// buttons from: that one returns only what a browser needs, this one returns
// what an operator needs to set a provider up.
//
// No endpoint here ever returns a client secret. `hasClientSecret` reports
// whether one is stored; the value itself is write-only.

export const authProvidersHealth = () =>
  api.get('/sys/config/auth/providers/health')
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

/**
 * Save credentials for one provider.
 *
 * ⚠️ Omitting `clientSecret` (or sending it empty) LEAVES THE STORED ONE
 * ALONE — it does not clear it. The form cannot display a secret it never
 * receives, so a re-save that only changes the redirect URI would otherwise
 * silently un-configure a working provider. Pass `clearClientSecret: true` to
 * actually remove it.
 */
export const putAuthProviderCredentials = (provider, payload) =>
  api.put(`/sys/config/auth/${provider}/credentials`, payload)
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

export const patchAuthProviderEnabled = (provider, enabled) =>
  api.patch(`/sys/config/auth/${provider}/enable`, { enabled })
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

export const patchAuthProviderAllowRegistration = (provider, allowRegistration) =>
  api.patch(`/sys/config/auth/${provider}/allow-registration`, { allowRegistration })
    .then((r) => (r?.data?.data ?? r?.data ?? {}));

export const patchAuthEnforceSocialEmailMatch = (enabled) =>
  api.patch('/sys/config/auth/enforce-social-email-match', { enabled })
    .then((r) => (r?.data?.data ?? r?.data ?? {}));
